import React, { useState, useContext, useEffect } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../SecurityConfig/AuthContext'; 
import Header from './Header'; 
import { endpoint } from '../../resource/Constant';

const SchoolSignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotDialogOpen, setForgotDialogOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const { setLoginDetails, loginDetails } = useContext(AuthContext);
  const navigate = useNavigate();
  

  useEffect(() => {
    if (loginDetails.isAuthenticated) navigate('/lessonplanner/dashboard');
  }, []);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);

    if (!email.trim()) {
      setError('Email is required');
      setLoading(false);
      return;
    }
    if (!password.trim()) {
      setError('Password is required');
      setLoading(false);
      return;
    }

    const requestBody = {
      email: email,
      password: password,
    };
     

    try {
      const response = await axios.post(`${endpoint}/signin`, requestBody);
      setMessage(response?.data?.message);

      // Store each value separately in localStorage
      if (response?.data?.email && response?.data?.jwt && response?.data?.userId) {
        localStorage.setItem('userId', response.data.userId);
        localStorage.setItem('email', response.data.email);
        localStorage.setItem('role', response.data.role);
        localStorage.setItem('school_token', response.data.jwt);

        setLoginDetails({
          isAuthenticated: true,
          email: response.data.email,
          jwt: response.data.jwt,
          role: response.data.role,
          userId: response.data.userId,
        });
      }

      setLoading(false);
      navigate('/lessonplanner/dashboard');
      
    } catch (error) {
      setError(error?.response?.data?.message || 'Sign in failed. Please try again.');
      setLoading(false);
    }
  };

  // Forgot Password Dialog handlers
  const handleForgotOpen = () => {
    setForgotDialogOpen(true);
    setForgotEmail('');
    setForgotError('');
  };

  const handleForgotClose = () => {
    setForgotDialogOpen(false);
    setForgotEmail('');
    setForgotError('');
  };

  const handleForgotSubmit = async () => {
    setForgotError('');
    setForgotLoading(true);
    try {
      await axios.post(`${endpoint}/forgotpassword`, null, {
        params: { email: forgotEmail }
      });
      setForgotLoading(false);
      setForgotDialogOpen(false);
      // Navigate to ChangePasswordWithOtp component after successful request
      navigate('/lessonplanner/forgot-password', { state: { email: forgotEmail } });
    } catch (err) {
      setForgotError(
        err?.response?.data?.message || 'Failed to send OTP. Please check your email and try again.'
      );
      setForgotLoading(false);
    }
  };

  return (
    <>
    <Header/>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f4f6f8',
        flexDirection: 'column',
        position: 'fixed',
        width: '100%',
      }}
    >
      <Paper sx={{ width: '100%', maxWidth: '470px', borderRadius: '12px', backgroundColor: 'white', boxShadow: 3, padding: 4 }}>
        <Typography variant="h6" sx={{ mb: 1, textAlign: 'center', fontWeight: 'bold', color: '#1976d2', fontSize: '[1.5rem]' }}>
          School Sign In
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: '#64748b' }}>
          Sign in to your school account
        </Typography>

        <form onSubmit={handleSignIn}>
          <TextField
            type="email"
            label="Email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2, borderRadius: '12px' }}
            required
          />
          <TextField
            type="password"
            label="Password"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mb: 3, borderRadius: '12px' }}
            required
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ py: 1.5, fontSize: '16px', borderRadius: '8px', textTransform: 'none', mt: 1 }}
            disabled={loading}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                Signing In...
              </>
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        {message && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {message}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2">
            Don't have an account?{' '}
            <Link to="/lessonplanner/signup" style={{ color: '#1976d2', textDecoration: 'underline', fontWeight: 500 }}>
              Sign Up
            </Link>
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            <span
              onClick={handleForgotOpen}
              style={{ color: '#1976d2', textDecoration: 'underline', fontWeight: 500, cursor: 'pointer' }}
            >
              Forgot Password?
            </span>
          </Typography>
        </Box>
      </Paper>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotDialogOpen} onClose={handleForgotClose}>
        <DialogTitle>Forgot Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter your email to receive an OTP for password reset.
          </Typography>
          <TextField
            label="Email"
            type="email"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            fullWidth
            required
            sx={{ mb: 2 }}
          />
          {forgotError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {forgotError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleForgotClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleForgotSubmit}
            variant="contained"
            color="primary"
            disabled={forgotLoading || !forgotEmail}
          >
            {forgotLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Send OTP'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </>
  );
};

export default SchoolSignIn;